import * as React from 'react';
import { useMemo, createElement, Fragment } from 'react';
import LinkifyIt from 'linkify-it';
import marked, { Renderer } from 'marked';
import { shortenLongText, includes } from '@livechat/data-utils';
import { hasUnsafeProtocol, getProtocol } from '@livechat/url-utils';

function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}

var linkify = new LinkifyIt();
var map = Array.prototype.map;
var ALLOWED_NODE_NAMES = ['A', 'UL', 'OL', 'LI', 'STRONG', 'EM', 'BR', 'DEL', 'CODE', 'PRE'];
var baseRenderer = new Renderer();
baseRenderer.link = function (href, title, text) {
  var linkifyMatch = linkify.match(text);
  if (linkifyMatch && linkifyMatch.length > 1) {
    return text;
  } else {
    return "<a href=\"" + href + "\" target=\"_blank\" rel=\"nofollow noopener\">" + text + "</a>";
  }
};
var preserveListsRenderer = new Renderer();
Object.assign(preserveListsRenderer, baseRenderer);

// After updating marked to 2.1.3 we can no longer extend Renderer class
preserveListsRenderer.list = function (body, ordered, start) {
  var index = start;
  return body.replace(LIST_ITEM_KEY, function () {
    return ordered ? index++ + ". " : "- ";
  });
};
preserveListsRenderer.listitem = function (text) {
  return "" + LIST_ITEM_KEY + text;
};

// List item key is used to mark the item text.
// It marks the place where list item sign should be rendered.
//
// The Renderer is called in "reversed" order (deeper nodes~ first),
// so we render list item first and then what we have rendered as list items is received by list rendering function.
// It's somewhat lossy transformation so to keep track of list items in rendered body
// We put a special token that can later be just replaced with a final list rendering function.
var LIST_ITEM_KEY = '%list-item%';

// removing other whitespace - if needed - is not responsibility of this module
var trimTrailingNewlines = function trimTrailingNewlines(str) {
  return str.replace(/\n+$/, '');
};
var isEmailAddress = function isEmailAddress(url) {
  var emailRegexp = /\S+@\S+\.\S+/;
  return emailRegexp.test(url);
};
function isDeepChildOf(node, nodeName) {
  var parent = node.parentNode;
  while (parent) {
    if (parent.nodeName === nodeName) {
      return true;
    }
    parent = parent.parentNode;
  }
  return false;
}

/**
 * Processes text node to React element.
 * If node is a child of an anchor element it is returned as is.
 * If node contains a link it is replaced with anchor element.
 * @param node
 * @param index
 * @param linkComponent
 */
function procesTextToReactElement(node, index, linkComponent) {
  if (isDeepChildOf(node, 'A')) {
    return node.textContent;
  }
  if (node.textContent === null) {
    return null;
  }
  var linkifyMatch = linkify.match(node.textContent);
  if (!linkifyMatch || linkifyMatch.length === 0) {
    return node.textContent;
  }
  var _Array$from$reduce = Array.from(linkifyMatch).reduce(function (_ref, match, index) {
      var elements = _ref[0],
        lastIndex = _ref[1];
      elements.push(node.textContent.slice(lastIndex, match.index), /*#__PURE__*/createElement(linkComponent != null ? linkComponent : 'a', {
        href: match.url,
        target: '_blank',
        rel: 'nofollow noopener'
      }, match.text));
      if (index === linkifyMatch.length - 1) {
        elements.push(node.textContent.slice(match.lastIndex));
      }
      return [elements, match.lastIndex];
    }, [[], 0]),
    elements = _Array$from$reduce[0];
  return /*#__PURE__*/createElement.apply(React, [Fragment, {
    key: index
  }].concat(elements));
}

/**
 * Maps DOM nodes list to corresponding React components.
 * Nodes which are not allowed are forced to be in text type.
 * @param nodes list of nodes to map
 */
function mapNodeListToReactElements(nodes, linkComponent, limit) {
  var totalLength = 0;
  var limitReached = false;
  var iterate = function iterate(_nodes) {
    return map.call(_nodes, function (node, index) {
      if (node.nodeName === '#text') {
        if (!limit) {
          return procesTextToReactElement(node, index, linkComponent);
        }
        if (!limitReached) {
          if (node.textContent && node.textContent.length + totalLength < limit) {
            totalLength += node.textContent.length;
            return procesTextToReactElement(node, index, linkComponent);
          }
          limitReached = true;
          node.textContent = shortenLongText(limit - totalLength, node.textContent);
          return procesTextToReactElement(node, index, linkComponent);
        }
        return null;
      }
      if (!includes(node.nodeName, ALLOWED_NODE_NAMES)) {
        return node.hasChildNodes() ? iterate(node.childNodes) : null;
      }
      var baseProps = _extends({
        key: index
      }, node.hasChildNodes() && {
        children: iterate(node.childNodes)
      });
      if (node.nodeName === 'A') {
        var href = node.getAttribute('href');
        if (!href || hasUnsafeProtocol(href)) {
          return iterate(node.childNodes);
        }
        if (!isEmailAddress(href) && !getProtocol(href)) {
          href = "https://" + href;
        }
        if (linkComponent) {
          return /*#__PURE__*/createElement(linkComponent, _extends({
            href: href
          }, baseProps));
        }
        var props = _extends({}, baseProps, {
          href: href,
          target: '_blank',
          rel: 'nofollow noopener'
        });
        return /*#__PURE__*/createElement('a', props);
      }
      return /*#__PURE__*/createElement(node.nodeName.toLowerCase(), baseProps);
    });
  };
  return iterate(nodes);
}

/**
 * Wipeouts markdown formatting from template leaving only text content
 * @param template markdown template
 */
function wipeoutMD(template) {
  var html = marked(template, {
    renderer: preserveListsRenderer
  });
  var parser = new DOMParser();
  var _parser$parseFromStri = parser.parseFromString(html, 'text/html'),
    body = _parser$parseFromStri.body;
  // for empty string used as template IE11 returns a document with null as body  ¯\_(ツ)_/¯
  return body ? trimTrailingNewlines(body.textContent || '') : '';
}
var ReactMD = function ReactMD(_ref2) {
  var _root$props, _root$component;
  var template = _ref2.template,
    root = _ref2.root,
    preserveLists = _ref2.preserveLists,
    limit = _ref2.limit,
    _ref2$linkComponent = _ref2.linkComponent,
    linkComponent = _ref2$linkComponent === void 0 ? null : _ref2$linkComponent;
  var rootProps = (_root$props = root == null ? void 0 : root.props) != null ? _root$props : {};
  var RootComponent = (_root$component = root == null ? void 0 : root.component) != null ? _root$component : Fragment;
  var children = useMemo(function () {
    try {
      var parser = new DOMParser();
      var html = trimTrailingNewlines(marked(template, preserveLists ? {
        renderer: preserveListsRenderer
      } : {
        renderer: baseRenderer
      }));
      var _parser$parseFromStri2 = parser.parseFromString(html, 'text/html'),
        body = _parser$parseFromStri2.body;

      // for empty string used as template IE11 returns a document with null as body  ¯\_(ツ)_/¯
      return body ? mapNodeListToReactElements(body.childNodes, linkComponent, limit) : '';
    } catch (error) {
      console.error("ReactMD: error while parsing template: '" + template + "'", error);
      return template;
    }
  }, [template, preserveLists, limit, linkComponent]);
  return /*#__PURE__*/createElement(RootComponent, rootProps, children);
};

export default ReactMD;
export { wipeoutMD };
